import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useModulos } from "@/hooks/use-modulos";
import { formatCurrency } from "@/lib/utils";
import {
  Receipt,
  Loader2,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Plug,
  Save,
} from "lucide-react";

interface BlingStatus {
  conectado: boolean;
  expiresAt: string | null;
  servicoIdPadrao: string | null;
}

interface CompraPendente {
  id: string;
  empresa_id: string;
  itens: Array<{ tipo_veiculo?: string; quantidade?: number }>;
  valor_total: string;
  criado_em: string;
}

interface NfeEmissao {
  id: string;
  compraId: string;
  tipo: string;
  valor: string;
  status: string;
  numeroNota: string | null;
  linkPdf: string | null;
  erro: string | null;
  gateway: string;
  criadoEm: string;
}

function ConexaoBling({ token }: { token: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [servicoId, setServicoId] = useState("");

  const { data: status, isLoading } = useQuery<BlingStatus>({
    queryKey: ["/api/bling/status"],
    queryFn: async () => {
      const res = await fetch("/api/bling/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erro ao buscar status do Bling");
      return res.json();
    },
  });

  const conectar = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/bling/conectar/iniciar", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erro ao iniciar conexão");
      return res.json() as Promise<{ authorizeUrl: string }>;
    },
    onSuccess: (data) => {
      window.open(data.authorizeUrl, "_blank", "noopener,noreferrer");
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const salvarServico = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/bling/servico-padrao", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ servicoId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Erro ao salvar serviço padrão");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bling/status"] });
      toast({ title: "Serviço padrão salvo" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  return (
    <Card className="border-none shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Plug className="w-4 h-4 text-teal-600" />
          Conexão Bling
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        ) : (
          <>
            <div className="flex items-center gap-3">
              <Badge variant={status?.conectado ? "default" : "outline"} className="gap-1">
                {status?.conectado ? (
                  <CheckCircle2 className="w-3 h-3" />
                ) : (
                  <XCircle className="w-3 h-3" />
                )}
                {status?.conectado ? "Conectado" : "Não conectado"}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                onClick={() => conectar.mutate()}
                disabled={conectar.isPending}
              >
                {conectar.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                )}
                {status?.conectado ? "Reconectar" : "Conectar ao Bling"}
              </Button>
            </div>

            <div>
              <label className="text-xs text-slate-500 mb-1 block">
                Código do serviço (cadastrado no Bling para "vistoria/laudo")
                {status?.servicoIdPadrao && (
                  <span className="text-emerald-600 ml-1">✓ configurado</span>
                )}
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder={status?.servicoIdPadrao ?? "ID do serviço no Bling"}
                  value={servicoId}
                  onChange={(e) => setServicoId(e.target.value)}
                  className="font-mono text-xs h-8 max-w-xs"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs shrink-0"
                  disabled={salvarServico.isPending || !servicoId.trim()}
                  onClick={() => salvarServico.mutate()}
                >
                  {salvarServico.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Save className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const STATUS_LABEL: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pendente: { label: "Pendente", variant: "outline" },
  emitida: { label: "Emitida", variant: "default" },
  erro: { label: "Erro", variant: "destructive" },
  cancelada: { label: "Cancelada", variant: "secondary" },
};

export default function NotaFiscal() {
  const { profile, session, loading } = useAuth();
  const { modulos, isLoading: loadingModulos } = useModulos();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const token = session?.access_token ?? "";

  const habilitado = modulos.creditosPrePagos && modulos.nfeAtivado;

  const { data: pendentes, isLoading: loadingPendentes } = useQuery<CompraPendente[]>({
    queryKey: ["/api/nfe/pendentes"],
    queryFn: async () => {
      const res = await fetch("/api/nfe/pendentes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erro ao buscar compras pendentes");
      return res.json();
    },
    enabled: !!token && habilitado,
  });

  const { data: historico, isLoading: loadingHistorico } = useQuery<NfeEmissao[]>({
    queryKey: ["/api/nfe/historico"],
    queryFn: async () => {
      const res = await fetch("/api/nfe/historico", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erro ao buscar histórico");
      return res.json();
    },
    enabled: !!token && habilitado,
  });

  const emitir = useMutation({
    mutationFn: async (compraId: string) => {
      const res = await fetch("/api/nfe/emitir", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ compraId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Erro ao emitir NF-e");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/nfe/pendentes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/nfe/historico"] });
      toast({ title: "Emissão processada — confira o status no histórico" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  if (loading || loadingModulos) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Shell>
    );
  }

  if (!habilitado) {
    navigate("/");
    return null;
  }

  return (
    <Shell>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Receipt className="w-5 h-5 text-primary" />
          </div>
          <h1 className="text-3xl font-bold font-display text-slate-900">Nota Fiscal</h1>
        </div>
        <p className="text-muted-foreground mt-1 ml-12">
          Emissão de NFS-e (via Bling) das compras de crédito
        </p>
      </div>

      <div className="max-w-3xl space-y-6">
        <ConexaoBling token={token} />

        <Card className="border-none shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Compras pendentes de NF-e</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loadingPendentes ? (
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            ) : !pendentes || pendentes.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                Nenhuma compra pendente de emissão.
              </p>
            ) : (
              pendentes.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3"
                >
                  <div>
                    <p className="text-sm font-medium">{formatCurrency(Number(c.valor_total))}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(c.criado_em).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => emitir.mutate(c.id)}
                    disabled={emitir.isPending}
                  >
                    {emitir.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : null}
                    Emitir NF-e
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-none shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Histórico</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {loadingHistorico ? (
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            ) : !historico || historico.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                Nenhuma NF-e emitida ainda.
              </p>
            ) : (
              historico.map((n) => (
                <div
                  key={n.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3"
                >
                  <div>
                    <p className="text-sm font-medium">
                      {n.tipo === "vistoriadora_parceiro" ? "Vistoriadora → Parceiro" : "Owner → Vistoriadora"}
                      {" · "}
                      {formatCurrency(Number(n.valor))}
                    </p>
                    {n.erro && <p className="text-xs text-destructive">{n.erro}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={STATUS_LABEL[n.status]?.variant ?? "outline"}>
                      {STATUS_LABEL[n.status]?.label ?? n.status}
                    </Badge>
                    {n.tipo === "owner_vistoriadora" && (
                      <Badge variant="outline" className="text-[10px] uppercase">
                        {n.gateway === "asaas" ? "Asaas" : "Bling"}
                      </Badge>
                    )}
                    {n.linkPdf && (
                      <Button size="sm" variant="ghost" asChild>
                        <a href={n.linkPdf} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}
